// ============================================================================
// src/pages/ResetPasswordPage.tsx
// ============================================================================
import React, { useState } from "react";
import { useResetPasswordMutation } from "../services/auth";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";

export default function ResetPasswordPage() {
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const navigate = useNavigate();
  const locState = (useLocation().state || {}) as {
    email?: string;
    token?: string;
  };
  const [searchParams] = useSearchParams();

  // Get email and token from URL params or location state
  const [email, setEmail] = useState(
    searchParams.get("email") || locState.email || ""
  );
  const [token, setToken] = useState(
    searchParams.get("token") || locState.token || ""
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFeedback(null);

    if (!email || !token || !newPassword) {
      setError("Please provide email, token and new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      const response = await resetPassword({
        email,
        token,
        newPassword,
      }).unwrap();

      setFeedback(
        response.message ||
          "Password reset successfully! Redirecting to login..."
      );

      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      setError(
        err?.data?.error ||
          err?.data?.message ||
          "Failed to reset password. The link may have expired."
      );
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white shadow-lg rounded-lg p-8"
      >
        <h2 className="text-2xl font-bold mb-2 text-gray-900">
          Set a new password
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Enter your new password below. Make it strong and secure.
        </p>

        {feedback && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">{feedback}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {!locState.email && !searchParams.get("email") && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        )}

        {!locState.token && !searchParams.get("token") && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reset token
            </label>
            <input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="At least 8 characters"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm new password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Re-enter your password"
          />
        </div>

        <button
          type="submit"
          className="w-full px-4 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-60 transition-colors"
          disabled={isLoading}
        >
          {isLoading ? "Resetting..." : "Reset password"}
        </button>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-gray-600 hover:text-gray-700"
          >
            Back to login
          </Link>
        </div>
      </form>
    </div>
  );
}
